import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, AlertTriangle } from 'lucide-react';
import { Modal } from './ui/Modal';
import { parseImportFile } from '../utils/collectionImport';
import { parseImportFolder } from '../utils/collectionImport/folder/parseImportFolder';
import {
  readFolderFromFileList,
  readFolderFromEntry,
  getDroppedDirectory,
} from '../utils/collectionImport/folder/readFolderFiles';
import { isAcceptedFile, isHarFile } from '../utils/collectionImport/acceptedFiles';
import ImportFileZone from './import/ImportFileZone';
import ImportPreview from './import/ImportPreview';
import { recordFeature, ACTION } from '../services/telemetry';
import './import/importModal.css';

export default function ImportCollectionModal({
  isOpen,
  onClose,
  onImport,
  onImportEnvironment,
  importing = false,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const readIdRef = useRef(0);   // only the newest FileReader may write the preview
  const [parseError, setParseError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [nameOverrides, setNameOverrides] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [showHarWarning, setShowHarWarning] = useState(false);
  const [file, setFile] = useState(null); // { name, size, folder? } — the dialog says WHICH file it is looking at
  const [includeEnvironments, setIncludeEnvironments] = useState(true);

  const resetState = useCallback(() => {
    setParseError(null);
    setPreview(null);
    setSelectedIndices(new Set());
    setNameOverrides({});
    setDragOver(false);
    setShowHarWarning(false);
    setFile(null);
    setIncludeEnvironments(true);
    dragCounterRef.current = 0;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const processFile = useCallback((file) => {
    if (!file) return;
    if (!isAcceptedFile(file)) {
      setPreview(null);
      setFile(null);
      setParseError(t('import.unsupportedFileType'));
      return;
    }
    // The row now NAMES the file, so the preview under it must never belong to a different one:
    // clear it before the read starts, and again if the read fails. Otherwise "Replace" shows the
    // new name over the old collections — with Import still enabled — and imports the old file.
    setParseError(null);
    setPreview(null);
    setSelectedIndices(new Set());
    setNameOverrides({});
    setFile({ name: file.name, size: file.size });
    setShowHarWarning(isHarFile(file));
    setIncludeEnvironments(true);
    // …and two reads can finish out of order, so only the newest one may write.
    readIdRef.current += 1;
    const readId = readIdRef.current;
    const reader = new FileReader();
    reader.onload = () => {
      if (readId !== readIdRef.current) return;
      try {
        const result = parseImportFile(String(reader.result || ''), file.name);
        setPreview(result);
        setSelectedIndices(new Set((result.collections || []).map((_, i) => i)));
        setNameOverrides({});
      } catch (err) {
        setPreview(null);
        setParseError(err.message || t('import.parseFailed'));
      }
    };
    reader.onerror = () => {
      if (readId !== readIdRef.current) return;
      setPreview(null);
      setSelectedIndices(new Set());
      setFile(null);
      setParseError(t('import.readFailed'));
    };
    reader.readAsText(file);
  }, [t]);

  /**
   * A Bruno collection FOLDER: same preview, same Import button, same newest-wins guard as a
   * file (one import surface — owner, 07.08 and 27.09.2026). `read` is the already-started read.
   */
  const processFolder = useCallback((read) => {
    setParseError(null);
    setPreview(null);
    setSelectedIndices(new Set());
    setNameOverrides({});
    setShowHarWarning(false);
    setIncludeEnvironments(true);
    readIdRef.current += 1;
    const readId = readIdRef.current;
    read
      .then(({ rootName, files }) => {
        if (readId !== readIdRef.current) return;
        const size = files.reduce((n, f) => n + f.text.length, 0);
        setFile({ name: `${rootName || 'folder'}/`, size, folder: true });
        const result = parseImportFolder(files, rootName);
        setPreview(result);
        setSelectedIndices(new Set(result.collections.map((_, i) => i)));
      })
      .catch((err) => {
        if (readId !== readIdRef.current) return;
        setPreview(null);
        setFile(null);
        setParseError(err?.message || t('import.folderReadFailed'));
      });
  }, [t]);

  const openFilePicker = useCallback(() => {
    if (!importing) {
      fileInputRef.current?.click();
    }
  }, [importing]);

  const openFolderPicker = useCallback(() => {
    if (!importing) {
      folderInputRef.current?.click();
    }
  }, [importing]);

  // React does not type `webkitdirectory` / `directory`; set them on the node.
  const setFolderInput = useCallback((node) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
  }, []);

  const handleFolderChange = (e) => {
    const files = e.target.files;
    if (files && files.length) processFolder(readFolderFromFileList(Array.from(files)));
    e.target.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);

    // A dropped FOLDER. The entry must be taken synchronously, here — the item list is dead
    // once this handler returns.
    const dir = getDroppedDirectory(e.dataTransfer?.items);
    if (dir) {
      processFolder(readFolderFromEntry(dir));
      return;
    }

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const toggleCollection = (index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const isEnvironmentPreview = preview?.kind === 'environment';

  const handleImport = async () => {
    if (!preview) return;
    recordFeature(ACTION.IMPORT);   // identifier-free: import was used today
    if (isEnvironmentPreview) {
      const envs = preview.environments?.length ? preview.environments : [preview.environment];
      // The first one is imported (and selected) exactly as before; any others are added quietly.
      // One after another: each one counts against the plan's cap (Plans v2).
      for (let i = 0; i < envs.length; i += 1) {
        await (i === 0 ? onImportEnvironment?.(envs[i]) : onImportEnvironment?.(envs[i], { silent: true, select: false }));
      }
      return;
    }
    if (selectedIndices.size === 0) return;
    const toImport = preview.collections
      .map((col, i) => {
        if (!selectedIndices.has(i)) return null;
        const override = nameOverrides[i];
        return {
          ...col,
          name: override != null && override.trim() ? override.trim() : col.name,
        };
      })
      .filter(Boolean);
    // Collections FIRST, environments only once they landed (same order as the sidebar drop).
    // handleImportCollections resolves null on failure and leaves this dialog open with its
    // preview, so a retry must not have already added "Local", "Local (2)", …
    const envs = includeEnvironments ? preview.environments || [] : [];
    const imported = await onImport?.(toImport, preview.warnings || []);
    if (!imported) return;
    // O4: environments that came with the collection, unless unticked — never auto-selected.
    for (const env of envs) await onImportEnvironment?.(env, { silent: true, select: false });
  };

  const dragHandlers = { onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop };
  const canImport = !importing && !!preview && (isEnvironmentPreview || selectedIndices.size > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('import.title')}
      subtitle={t('import.subtitle', { defaultValue: 'From Postman, Insomnia, Bruno, Hoppscotch, OpenAPI / Swagger or HAR' })}
      customIcon={<Upload size={24} className="text-blue-500" />}
      preventBackdropClose={importing}
      preventEscapeClose={importing}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.yaml,.yml,.har,.bru,application/json,application/x-yaml,text/yaml,text/x-yaml,application/har+json,text/plain"
        tabIndex={-1}
        aria-hidden
        disabled={importing}
        onChange={handleFileChange}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      {/* The folder picker: a SECOND hidden input only because `webkitdirectory` makes an input
          pick folders and nothing else. The visible surface stays one zone. */}
      <input
        ref={setFolderInput}
        type="file"
        data-testid="import-folder-input"
        tabIndex={-1}
        aria-hidden
        disabled={importing}
        onChange={handleFolderChange}
        style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
      />

      <div className="space-y-4 imp-body">
        <ImportFileZone file={preview ? file : null} formatLabel={preview?.formatLabel} dragOver={dragOver} busy={importing} onPick={openFilePicker} onPickFolder={openFolderPicker} dragHandlers={dragHandlers} t={t} />

        {parseError && <p className="imp-error" role="alert">{parseError}</p>}

        {showHarWarning && !parseError && (
          <div className="imp-notes" role="alert">
            <div className="imp-notes__title"><AlertTriangle size={13} aria-hidden />{t('import.harSensitiveDataWarning')}</div>
          </div>
        )}

        {preview ? (
          <ImportPreview
            preview={preview}
            selected={selectedIndices}
            names={nameOverrides}
            busy={importing}
            onToggle={toggleCollection}
            onRename={(i, value) => setNameOverrides((prev) => ({ ...prev, [i]: value }))}
            includeEnvironments={includeEnvironments}
            onToggleEnvironments={() => setIncludeEnvironments((v) => !v)}
            t={t}
          />
        ) : (
          <p className="imp-formats">{t('import.supportedFormats')}</p>
        )}

        {/* The "Import Bruno folder" BUTTON was removed 07.08.2026 (owner's call: one import surface,
            not two). A Bruno collection folder now comes in through the same zone — dropped on it, or
            via its inline "Choose folder" link (owner, 27.09.2026) — and the same preview. */}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!canImport}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {importing ? t('import.importing') : t('import.importButton')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
